import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchReadState, patchReadState } from '@apis/read-state';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

type ReadMap = Record<string, number>;
type CacheShape = { read: ReadMap } | undefined;

export interface IUseReadStateResult {
  isUnseen: (key: string) => boolean;
  unreadComments: (key: string, commentCount: number) => number;
  markSeen: (key: string, commentCount: number) => void;
  markAllRead: (items: ReadonlyArray<{ key: string; commentCount: number }>) => void;
  isLoading: boolean;
}

export function useReadState(siteId: string | null): IUseReadStateResult {
  const queryClient = useQueryClient();
  const queryKey = ['read-state', siteId] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchReadState(siteId!, signal),
    enabled: !!siteId,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    // Silent by design: an unsaved read mark only leaves the item marked unread.
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (read: ReadMap) => {
      if (!siteId) throw new Error('markSeen called without siteId');
      return patchReadState(siteId, read);
    },
    onMutate: async (patch: ReadMap) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CacheShape>(queryKey);
      // Merge by max: a read count only grows, so a slow/reordered mutation can
      // never lower it. Mirrors the backend merge.
      queryClient.setQueryData<CacheShape>(queryKey, (current) => {
        const next: ReadMap = { ...(current?.read ?? {}) };
        for (const [k, n] of Object.entries(patch)) next[k] = Math.max(next[k] ?? 0, n);
        return { read: next };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    // No onSuccess reconcile: the optimistic (max-merged) value is exactly what we
    // want, and replacing the whole cache with a possibly-stale/reordered server
    // response would clobber a newer markSeen (e.g. an earlier open's PATCH landing
    // after a just-sent comment's). The backend also merges by max, so cache and
    // server agree; the next GET reconciles.
  });

  const readMap: ReadMap = data?.read ?? {};

  const isUnseen = (key: string): boolean => !(key in readMap);

  const unreadComments = (key: string, commentCount: number): number =>
    Math.max(0, commentCount - (readMap[key] ?? 0));

  const markSeen = (key: string, commentCount: number): void => {
    if (!siteId) return;
    mutation.mutate({ [key]: commentCount });
  };

  const markAllRead = (items: ReadonlyArray<{ key: string; commentCount: number }>): void => {
    if (!siteId || items.length === 0) return;
    const patch: ReadMap = {};
    for (const item of items) {
      patch[item.key] = item.commentCount;
    }
    mutation.mutate(patch);
  };

  return { isUnseen, unreadComments, markSeen, markAllRead, isLoading };
}
